import type { TooltipContent } from "../render/RenderContext.js";
import type { TextAlign } from "./Label.js";

/** What a cell holds, and therefore how it is edited. */
export type DataGridCellType = "text" | "number" | "check" | "choice";

export type SortDirection = "asc" | "desc" | null;

/**
 * One column, bound to a property of the row object.
 *
 * `key` does the binding; `get`/`set` override it when the value is computed
 * or lives somewhere other than a plain property.
 */
export interface DataGridColumn<T = Record<string, unknown>> {
  key: string;
  header: string;
  width?: number;
  minWidth?: number;
  align?: TextAlign;
  type?: DataGridCellType;
  /** Cells in this column cannot be edited. */
  readOnly?: boolean;
  /** Options for a `choice` column. */
  choices?: string[];
  /** Options for a `choice` column that differ by row. Wins over `choices`. */
  choicesFor?: (row: T) => string[];
  /**
   * A `choice` column whose editor also takes typing: the options are
   * suggestions, and whatever text is committed is accepted.
   */
  freeText?: boolean;
  /**
   * What the editor opens with. Defaults to the display text; set it when the
   * cell shows more than the editable part, such as a file name whose extension
   * is not the user's to change.
   */
  editText?: (row: T) => string;
  /** Display text. Defaults to the value stringified, blank for null. */
  format?: (value: unknown, row: T) => string;
  /** Read the value. Defaults to `row[key]`. */
  get?: (row: T) => unknown;
  /** Write it back. Defaults to `row[key] = value`. */
  set?: (row: T, value: unknown) => void;
  /** Ordering. Defaults to numeric, boolean or locale string compare. */
  compare?: (a: unknown, b: unknown) => number;
  /** Clicking the header sorts by this column. Defaults to true. */
  sortable?: boolean;
  /** What hovering a cell says. Defaults to the cell's own text; null says nothing. */
  tooltip?: (row: T) => TooltipContent | null;
}

const DEFAULT_WIDTH = 110;

export function columnWidth<T>(column: DataGridColumn<T>): number {
  return Math.max(columnMinWidth(column), column.width ?? DEFAULT_WIDTH);
}

export function columnMinWidth<T>(column: DataGridColumn<T>): number {
  return column.minWidth ?? 32;
}

export function cellValue<T>(column: DataGridColumn<T>, row: T): unknown {
  if (column.get) return column.get(row);
  return (row as Record<string, unknown>)[column.key];
}

export function setCellValue<T>(column: DataGridColumn<T>, row: T, value: unknown): void {
  if (column.set) column.set(row, value);
  else (row as Record<string, unknown>)[column.key] = value;
}

/** The text drawn in a cell. Checkbox columns draw a box instead. */
export function cellText<T>(column: DataGridColumn<T>, row: T): string {
  const value = cellValue(column, row);
  if (column.format) return column.format(value, row);
  if (value === null || value === undefined) return "";
  return String(value);
}

/** The text an editor opens with. */
export function cellEditText<T>(column: DataGridColumn<T>, row: T): string {
  return column.editText ? column.editText(row) : cellText(column, row);
}

/** What a `choice` cell offers for this row. */
export function cellChoices<T>(column: DataGridColumn<T>, row: T | undefined): string[] {
  if (column.choicesFor && row !== undefined) return column.choicesFor(row);
  return column.choices ?? [];
}

export function isEditable<T>(column: DataGridColumn<T>): boolean {
  return column.readOnly !== true;
}

/**
 * Parse what the user typed back into the column's type.
 *
 * Returns `undefined` to reject the edit, which is how a number column
 * refuses to store "abc" rather than writing NaN into the data.
 */
export function parseCell<T>(
  column: DataGridColumn<T>,
  text: string,
  row?: T,
): unknown | undefined {
  switch (column.type ?? "text") {
    case "number": {
      const trimmed = text.trim();
      if (trimmed === "") return null;
      const n = Number(trimmed);
      return Number.isFinite(n) ? n : undefined;
    }
    case "choice":
      if (column.freeText) return text;
      // Only an offered choice is accepted; anything else is a typo.
      return cellChoices(column, row).includes(text) ? text : undefined;
    case "check":
      return text === "true";
    case "text":
    default:
      return text;
  }
}

/** Default ordering: numbers and booleans by value, everything else by text. */
export function compareValues(a: unknown, b: unknown): number {
  if (a === b) return 0;
  if (a === null || a === undefined) return -1;
  if (b === null || b === undefined) return 1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") return (a ? 1 : 0) - (b ? 1 : 0);
  return (collator ??= new Intl.Collator(undefined, { numeric: true, sensitivity: "base" })).compare(
    String(a),
    String(b),
  );
}

/**
 * What `localeCompare` with these options builds and throws away on every call.
 * A sort makes n log n comparisons, so one shared collator is most of its cost.
 */
let collator: Intl.Collator | undefined;

/**
 * Row indices in display order.
 *
 * Sorting is a permutation of indices rather than a reordering of the caller's
 * array: the data they handed over is never shuffled underneath them, and a
 * cell edit still writes to the row object they expect.
 */
export function sortedOrder<T>(
  rows: readonly T[],
  column: DataGridColumn<T> | null,
  direction: SortDirection,
): number[] {
  const order = rows.map((_, i) => i);
  if (!column || !direction) return order;
  const compare = column.compare ?? compareValues;
  const sign = direction === "asc" ? 1 : -1;
  return order.sort((ia, ib) => {
    const r = compare(cellValue(column, rows[ia]!), cellValue(column, rows[ib]!));
    // Ties keep their original order, so sorting is stable and repeatable.
    return r === 0 ? ia - ib : r * sign;
  });
}
